import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Six-digit one-time-password input. Renders six monospaced boxes
 * with auto-advance: typing a digit jumps to the next box, Backspace
 * clears the current cell and steps back. A 6-digit paste anywhere
 * in the row fills the whole input.
 *
 * Behaviour:
 *  - Calls `onComplete(code)` as soon as all six cells are filled —
 *    pages typically wire this to auto-submit.
 *  - Calls `onChange(partial)` on every keystroke so the parent can
 *    track the current value and reset on error.
 *  - When `error` is set, the boxes get a red border and the input
 *    is cleared on the next keystroke (so the user can re-type
 *    without manually deleting).
 *
 * Accessibility:
 *  - The whole group is wrapped in a fieldset with a hidden legend
 *    (`aria-label`).
 *  - Each cell carries `inputmode="numeric"`, `autocomplete="one-time-code"`,
 *    `maxLength={1}`, and `aria-label="Chiffre N sur 6"`.
 *  - Focus moves are programmatic; tab still works to escape the
 *    group.
 */
type Props = {
  /** Hidden legend / accessible name for the whole group. */
  ariaLabel: string;
  /** Called when the user has typed (or pasted) all 6 digits. */
  onComplete: (code: string) => void;
  /** Called on every keystroke with the current partial value. */
  onChange?: (partial: string) => void;
  /** When truthy, paints boxes red; cleared on next keystroke. */
  error?: string;
  /** Disable inputs while a verify is in flight. */
  disabled?: boolean;
  /** Auto-focus the first cell on mount (default true). */
  autoFocus?: boolean;
};

const N = 6;

export function OtpInput({
  ariaLabel,
  onComplete,
  onChange,
  error,
  disabled,
  autoFocus = true,
}: Props) {
  const [digits, setDigits] = useState<string[]>(Array(N).fill(''));
  const refs = useRef<Array<HTMLInputElement | null>>(Array(N).fill(null));

  // On mount, focus the first cell.
  useEffect(() => {
    if (autoFocus) {
      refs.current[0]?.focus();
    }
  }, [autoFocus]);

  // Clear the input when an error is freshly raised so the user
  // re-types without manually deleting.
  useEffect(() => {
    if (error) {
      setDigits(Array(N).fill(''));
      refs.current[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on error transition
  }, [error]);

  function setAt(i: number, ch: string): string[] {
    const next = digits.slice();
    next[i] = ch;
    return next;
  }

  function fireChanges(next: string[]): void {
    setDigits(next);
    onChange?.(next.join(''));
    if (next.every((d) => d.length === 1)) {
      onComplete(next.join(''));
    }
  }

  function handleChange(i: number, e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const ch = (raw.match(/\d/g) ?? []).pop() ?? '';
    const next = setAt(i, ch);
    fireChanges(next);
    if (ch && i < N - 1) {
      refs.current[i + 1]?.focus();
      refs.current[i + 1]?.select();
    }
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        fireChanges(setAt(i, ''));
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        fireChanges(setAt(i - 1, ''));
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < N - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const cleaned = (text.match(/\d/g) ?? []).slice(0, N).join('');
    if (!cleaned) return;
    e.preventDefault();
    const next = Array(N)
      .fill('')
      .map((_, i) => cleaned[i] ?? '');
    fireChanges(next);
    refs.current[Math.min(cleaned.length, N - 1)]?.focus();
  }

  return (
    <fieldset>
      <legend className="sr-only">{ariaLabel}</legend>
      {/*
        Responsive sizing: on viewports below md (768px) cells are
        44×48px with 6px gaps so 6 cells fit inside the typical
        max-w-md auth card on a 360px-wide phone (6×44 + 5×6 = 294px,
        comfortably inside the ~312px available after parent padding).
        Touch target stays at the WCAG min-touch 44px. On md+ we use
        the more generous 48×56px with 8px gaps.
      */}
      <div className="flex gap-1.5 md:gap-2 justify-center">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            value={d}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            disabled={disabled}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-label={`Chiffre ${i + 1} sur ${N}`}
            aria-invalid={error ? true : undefined}
            className={
              'w-11 h-12 md:w-12 md:h-14 ' +
              'text-center text-lg md:text-xl font-tabular font-medium ' +
              'border rounded-card bg-surface text-ink ' +
              'focus:outline-none transition-colors ' +
              (error
                ? 'border-semantic-error focus:border-semantic-error'
                : 'border-platinum focus:border-graphite')
            }
          />
        ))}
      </div>
      {error && (
        <p className="mt-3 text-center text-xs text-semantic-error" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
